import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [contractor, setContractor] = useState(null)
  const [permissions, setPermissions] = useState(null)

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchUserProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Only update user if session actually changed (avoid refetch on tab focus)
      if (session?.user) {
        // Check if this is a new user or just a token refresh
        setUser(prevUser => {
          // If we already have the full user profile, keep it
          if (prevUser?.id === session.user.id && prevUser?.role) {
            return prevUser
          }
          // Otherwise, fetch the profile
          fetchUserProfile(session.user.id)
          return session.user
        })
      } else {
        setUser(null)
        setContractor(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchUserProfile = async (userId) => {
    try {
      // Fetch user data with contractor info
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*, contractors(*)')
        .eq('id', userId)
        .single()

      if (userError) throw userError

      // Fetch user permissions
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (permissionsError) {
        console.error('Error fetching permissions:', permissionsError)
        // Set default permissions if none exist
        setPermissions(null)
      } else {
        setPermissions(permissionsData)
      }

      // Merge user profile data into the user object
      setUser(prevUser => ({
        ...prevUser,
        ...userData,
        contractors: userData?.contractors
      }))
      setContractor(userData?.contractors)
    } catch (error) {
      console.error('Error fetching user profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email, password, fullName, companyName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          company_name: companyName,
        },
      },
    })

    if (error) throw error
    return data
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) throw error
    return data
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setUser(null)
    setContractor(null)
    setPermissions(null)
  }

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  }

  // Check if contractor has active subscription or trial
  const hasActiveSubscription = () => {
    if (!contractor) return false

    const status = contractor.subscription_status
    const trialEndsAt = contractor.trial_ends_at ? new Date(contractor.trial_ends_at) : null
    const now = new Date()

    // Active subscription
    if (status === 'active') return true

    // Valid trial period (if trial_ends_at is set, check it's still valid)
    if (status === 'trialing') {
      // If trial_ends_at is null, assume trial is still valid (webhook may not have set it yet)
      if (!trialEndsAt) return true
      // If trial_ends_at is set, check if it's still in the future
      return trialEndsAt > now
    }

    return false
  }

  // Check if user is admin
  const isAdmin = () => {
    return user && user.role === 'admin'
  }

  // Refresh contractor data (call after updating contractor settings)
  const refreshContractor = async () => {
    if (!user?.id) return
    await fetchUserProfile(user.id)
  }

  const value = {
    user,
    contractor,
    permissions,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    hasActiveSubscription,
    isAdmin,
    refreshContractor,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
